import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import React, { Fragment } from "react";
import { DotLegalButton, DotLegalEmptyState, DotLegalOverflowMenu, DotLegalPageHeader, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../localization/useTranslation";
import { useRolesHooks } from "./Roles.hooks";
import { RoleViewModel } from "./Roles.types";
import Rocket from "../processingActivity/processingActivities/rocket.svg?react";
import { AddEditRoleDialog } from "./addEditRoleDialog/AddEditRoleDialog";
import CheckIcon from "@mui/icons-material/Check";
import RolePermissionDialog from "./rolePermissionDialog/RolePermissionDialog";
import DeleteRoleDialog from "./deleteRoleDialog/DeleteRoleDialog";
import { ChangeRoleStatusDialog } from "./changeRoleStatusDialog/ChangeRoleStatusDialog";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import FullAccessWarningIcon from "../common/components/fullAccessWarningIcon/FullAccessWarningIcon.tsx";
import PlanDialogButtonWrapper from "../plan/planDialogWrapper/planDialogWrapper";
import { getPlanOverflowMenuItem } from "../plan/planOverflowMenuItem/PlanOverflowMenuItem";
import { usePlanContext } from "../auth/planProvider/PlanProvider.tsx";
import { WhiteLockIcon } from "@common/icons.tsx";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export default function Roles() {
    const { translateString } = useTranslation();
    const { addonsFeatures } = usePlanContext();

    const {
        isLoading,
        data,
        refetchData,
        roleForEditing,
        setRoleForEditing,
        showRolesDialog,
        setShowRolesDialog,
        hasRoles,
        getBreadCrumbs,
        roleForStatusChange,
        setRoleForStatusChange,
        getFilterDataOptions,
        mapSearchFilter,
        roleForDeletion,
        setRoleForDeletion,
        rolePermission,
        setRolePermissions,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useRolesHooks();

    const getMenuItems = (viewModel: RoleViewModel) => {
        let menuItems = [];

        if (!viewModel.isStandardRole) {
            menuItems.push(
                getPlanOverflowMenuItem(
                    () => setRoleForEditing(viewModel),
                    addonsFeatures.userManagementPremium,
                    translateString("edit"),
                    translateString("upgradeToActivateAddOn")
                )
            );
        }

        if (viewModel.isStandardRole) {
            menuItems.push({
                menuItem: translateString("seePermissions"),
                onClick: () => {
                    setRolePermissions(viewModel);
                },
            });
        } else {
            menuItems.push(
                getPlanOverflowMenuItem(
                    () => setRolePermissions(viewModel),
                    addonsFeatures.userManagementPremium,
                    translateString("managePermissions"),
                    translateString("upgradeToActivateAddOn")
                )
            );
        }

        if (viewModel.isStandardRole) {
            menuItems.push(
                getPlanOverflowMenuItem(
                    () => setRoleForStatusChange(viewModel),
                    addonsFeatures.userManagementPremium,
                    viewModel.isActive ? translateString("deactivate") : translateString("activate"),
                    translateString("upgradeToActivateAddOn")
                )
            );
        }

        if (!viewModel.isStandardRole) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => setRoleForDeletion(viewModel),
            });
        }

        return menuItems;
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs()}
                rightComponent={
                    <PlanDialogButtonWrapper text={translateString("upgradeToActivateAddOn")} enabled={!addonsFeatures.userManagementPremium}>
                        <DotLegalButton
                            onClick={() => {
                                setShowRolesDialog(true);
                            }}
                            buttonType="primary"
                            size="medium"
                            disabled={!addonsFeatures.userManagementPremium}
                            toolTip={!addonsFeatures.userManagementPremium ? translateString("upgradeToActivateAddOn") : undefined}
                            endIcon={!addonsFeatures.userManagementPremium ? WhiteLockIcon : undefined}
                        >
                            {translateString("createRoles")}
                        </DotLegalButton>
                    </PlanDialogButtonWrapper>
                }
            />

            <TablePagePaper>
                {hasRoles ? (
                    <DotLegalTableWithFilterBar
                        internalScroll
                        hasOverflowMenu
                        filterBar={{
                            isLoading: isLoading,
                            filterData: getFilterDataOptions(),
                            fetchFilterData: true,
                            onMenuClose: (options) => mapSearchFilter(options),
                            onRemove: (options) => mapSearchFilter(options),
                            pinnedFilterIds: pinnedFilterIds,
                            onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                        }}
                        headers={getHeaders()}
                        rowsPerPage={100}
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        clickableRows
                        emptyText={translateString("roleEmptyState")}
                        data={data}
                        isLoading={isLoading}
                        renderRow={(row, i) => (
                            <TableRow key={i} inactive={!row.isActive} inactiveTooltip={translateString("deactivated")} onClick={() => setRolePermissions(row)}>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {row.name}
                                        {row.hasFullAccess && <FullAccessWarningIcon />}
                                    </Box>
                                </TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex" }}>
                                        {row.canOnlyAccessDataWhereResponsible ? <CheckIcon sx={{ height: "20px !important" }} /> : ""}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex" }}>{row.canBeChosenAsResponsible ? <CheckIcon sx={{ height: "20px !important" }} /> : ""}</Box>
                                </TableCell>
                                <TableCell>{row.isStandardRole && <CheckIcon sx={{ height: "20px !important" }} />}</TableCell>
                                <TableCell>{row.isActive ? translateString("active") : translateString("deactivated")}</TableCell>
                                <TableCell align={"right"}>{<DotLegalOverflowMenu items={getMenuItems(row)} />}</TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState
                        icon={<Rocket />}
                        text={
                            <React.Fragment>
                                <span>{translateString("noRoles")}</span>
                            </React.Fragment>
                        }
                    />
                )}
            </TablePagePaper>

            {roleForEditing && (
                <AddEditRoleDialog
                    onDialogClose={() => setRoleForEditing(undefined)}
                    onSave={() => {
                        refetchData();
                        setRoleForEditing(undefined);
                    }}
                    selectedRoleViewModel={roleForEditing}
                />
            )}

            {/* The dialog only refetches keys under /roles/{id}/permissions, so the grid must reload itself: full
                access is granted nowhere else, and the warning icon is derived from it. */}
            {rolePermission && (
                <RolePermissionDialog
                    id={rolePermission.id}
                    onDialogClose={() => {
                        setRolePermissions(undefined);
                        refetchData();
                    }}
                />
            )}

            {roleForDeletion && (
                <DeleteRoleDialog
                    id={roleForDeletion.id}
                    name={roleForDeletion.name}
                    onCloseDialog={() => setRoleForDeletion(undefined)}
                    onDeleteSuccess={() => {
                        refetchData();
                        setRoleForDeletion(undefined);
                    }}
                />
            )}
            {roleForStatusChange && (
                <ChangeRoleStatusDialog
                    selectedRole={roleForStatusChange}
                    onCloseDialog={() => setRoleForStatusChange(undefined)}
                    onSave={() => {
                        refetchData();
                        setRoleForStatusChange(undefined);
                    }}
                />
            )}

            {showRolesDialog && (
                <AddEditRoleDialog
                    onDialogClose={() => setShowRolesDialog(false)}
                    onSave={(viewModel?: RoleViewModel) => {
                        refetchData();
                        setShowRolesDialog(false);
                        setRolePermissions(viewModel);
                    }}
                />
            )}
        </Box>
    );

    function getHeaders() {
        let headers = Array<ITableHeader<RoleViewModel>>();
        headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });
        headers.push({
            property: "canOnlyAccessDataWhereResponsible",
            text: translateString("canOnlyAccessDataWhereResponsible"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({
            property: "canBeChosenAsResponsible",
            text: translateString("canBeChosenAsResponsible"),
            align: "left",
            showOnMobile: true,
        });
        headers.push({
            property: "isStandardRole",
            text: translateString("standardRole"),
            align: "left",
            showOnMobile: true,
        });

        headers.push({
            property: "isActive",
            text: translateString("status"),
            align: "left",
            showOnMobile: true,
        });

        return headers;
    }
}
